'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  AdGraphExplorePresetDto,
  AdGraphQueryDto,
  AdGraphResponseDto,
} from '@terra/shared-contracts-internal-network';
import { mergeAdGraphResponses } from '@terra/shared-contracts-internal-network';
import { terraProxyApiClient } from '../../clients/testerClient';
import {
  getInternalNetworkAdEntityKindCount,
  resolveFirstAvailableExplorePreset,
} from '../../utils/internal-network-graph.utils';
import { INTERNAL_NETWORK_GRAPH_KEY } from './useInternalNetworkGraph';
import { useInternalNetworkStats } from './useInternalNetworkStats';

const emptyGraph = (): AdGraphResponseDto => ({
  nodes: [],
  edges: [],
  truncated: false,
  totalCount: 0,
});

const normalizeGraphResponse = (
  graph: Awaited<
    ReturnType<typeof terraProxyApiClient.getInternalNetworkGraph>
  >,
): AdGraphResponseDto => ({
  ...graph,
  nodes: graph.nodes.map((node) => ({
    ...node,
    isOwned: node.isOwned ?? false,
  })),
});

export function useInternalNetworkExploreGraph({
  networkId,
  relationshipKinds,
}: {
  networkId: string | null;
  relationshipKinds?: readonly string[];
}) {
  const queryClient = useQueryClient();
  const hasAutoRunPresetRef = useRef(false);
  const requestSeqRef = useRef(0);
  const {
    stats,
    isLoading: isStatsLoading,
    refetch: refetchStats,
  } = useInternalNetworkStats(networkId);
  const crownJewelCount = stats?.crownJewelCount ?? 0;
  const ownedPrincipalCount = stats?.ownedPrincipalCount ?? 0;
  const [exploredGraph, setExploredGraph] = useState<AdGraphResponseDto | null>(
    null,
  );
  const [exploreRootId, setExploreRootId] = useState<string | null>(null);
  const [exploreDepth, setExploreDepth] = useState(1);
  const [activePreset, setActivePreset] =
    useState<AdGraphExplorePresetDto | null>(null);
  const [isExpanding, setIsExpanding] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const totalEntityCount = useMemo(
    () => getInternalNetworkAdEntityKindCount({ stats, kind: null }),
    [stats],
  );

  useEffect(() => {
    hasAutoRunPresetRef.current = false;
    requestSeqRef.current += 1;
    setExploredGraph(null);
    setExploreRootId(null);
    setExploreDepth(1);
    setActivePreset(null);
    setIsExpanding(false);
    setIsInitialLoading(true);
    setIsError(false);
  }, [networkId]);

  const fetchGraphQuery = useCallback(
    async (query: AdGraphQueryDto) => {
      const response = await queryClient.fetchQuery({
        queryKey: [INTERNAL_NETWORK_GRAPH_KEY, networkId, query],
        queryFn: () =>
          terraProxyApiClient.getInternalNetworkGraph(networkId ?? '', query),
      });

      return normalizeGraphResponse(response);
    },
    [queryClient, networkId],
  );

  const fetchNeighborhoodGraph = useCallback(
    async ({ nodeId, depth }: { nodeId: string; depth: number }) => {
      const query: AdGraphQueryDto = {
        mode: 'neighborhood',
        centerNodeId: nodeId,
        depth,
        relationshipKinds: relationshipKinds
          ? [...relationshipKinds]
          : undefined,
      };

      return fetchGraphQuery(query);
    },
    [fetchGraphQuery, relationshipKinds],
  );

  const runPresetQuery = useCallback(
    async (preset: AdGraphExplorePresetDto) => {
      const seq = ++requestSeqRef.current;

      setIsExpanding(true);
      setIsError(false);

      try {
        const query: AdGraphQueryDto =
          preset === 'crown-jewels'
            ? { mode: 'entry-points' }
            : {
                mode: 'preset',
                preset,
                relationshipKinds: relationshipKinds
                  ? [...relationshipKinds]
                  : undefined,
              };

        const nextGraph = await fetchGraphQuery(query);

        if (requestSeqRef.current !== seq) return;
        setExploredGraph(nextGraph);
        setExploreRootId(null);
        setExploreDepth(1);
        setActivePreset(preset);
      } catch (error: unknown) {
        if (requestSeqRef.current !== seq) return;
        console.warn(
          '[InternalNetworkExploreGraph] Preset query failed',
          error,
        );
        setIsError(true);
      } finally {
        if (requestSeqRef.current === seq) {
          setIsExpanding(false);
        }
      }
    },
    [fetchGraphQuery, relationshipKinds],
  );

  const relationshipKindsKey = relationshipKinds?.join(',') ?? '';

  const resetExplore = useCallback(async () => {
    setExploreRootId(null);
    setExploreDepth(1);
    setIsError(false);

    const preset = resolveFirstAvailableExplorePreset({
      crownJewelCount,
      ownedPrincipalCount,
      skipCrownJewels:
        crownJewelCount === 0 || (exploredGraph?.nodes.length ?? 0) === 0,
    });

    if (preset === 'crown-jewels' && (exploredGraph?.nodes.length ?? 0) > 0) {
      setActivePreset('crown-jewels');

      return;
    }

    await runPresetQuery(preset);
  }, [
    crownJewelCount,
    ownedPrincipalCount,
    exploredGraph?.nodes.length,
    runPresetQuery,
  ]);

  const replaceExploredGraph = useCallback(
    async ({ nodeId, depth }: { nodeId: string; depth: number }) => {
      const seq = ++requestSeqRef.current;

      setIsExpanding(true);
      setIsError(false);

      try {
        const neighborhood = await fetchNeighborhoodGraph({ nodeId, depth });

        if (requestSeqRef.current !== seq) return;
        setExploredGraph((current) => {
          if (!current) {
            return neighborhood;
          }

          return mergeAdGraphResponses(current, neighborhood);
        });
        setExploreRootId(nodeId);
        setExploreDepth(depth);
        setActivePreset(null);
      } catch (error: unknown) {
        if (requestSeqRef.current !== seq) return;
        console.warn(
          '[InternalNetworkExploreGraph] Neighborhood fetch failed',
          error,
        );
        setIsError(true);
      } finally {
        if (requestSeqRef.current === seq) {
          setIsExpanding(false);
        }
      }
    },
    [fetchNeighborhoodGraph],
  );

  const lastRelationshipKindsKeyRef = useRef(relationshipKindsKey);

  useEffect(() => {
    if (lastRelationshipKindsKeyRef.current === relationshipKindsKey) {
      return;
    }

    lastRelationshipKindsKeyRef.current = relationshipKindsKey;

    if (exploreRootId) {
      setExploredGraph(null);
      void replaceExploredGraph({ nodeId: exploreRootId, depth: exploreDepth });

      return;
    }

    if (activePreset && activePreset !== 'crown-jewels') {
      setExploredGraph(null);
      void runPresetQuery(activePreset);
    }
  }, [
    relationshipKindsKey,
    activePreset,
    exploreRootId,
    exploreDepth,
    replaceExploredGraph,
    runPresetQuery,
  ]);

  const mergeNodeNeighborhood = useCallback(
    async (nodeId: string): Promise<boolean> => {
      const seq = ++requestSeqRef.current;

      setIsExpanding(true);
      setIsError(false);

      try {
        const neighborhood = await fetchNeighborhoodGraph({
          nodeId,
          depth: 1,
        });

        if (requestSeqRef.current !== seq) {
          return false;
        }

        setExploredGraph((current) => {
          const base = current ?? emptyGraph();

          return mergeAdGraphResponses(base, neighborhood);
        });
        setActivePreset(null);

        return true;
      } catch (error: unknown) {
        if (requestSeqRef.current !== seq) {
          return false;
        }

        console.warn('[InternalNetworkExploreGraph] Expand node failed', error);
        setIsError(true);

        return false;
      } finally {
        if (requestSeqRef.current === seq) {
          setIsExpanding(false);
        }
      }
    },
    [fetchNeighborhoodGraph],
  );

  const exploreFromSearch = useCallback(
    async (nodeId: string) => {
      await replaceExploredGraph({ nodeId, depth: 1 });
    },
    [replaceExploredGraph],
  );

  const expandSelectedNode = useCallback(
    async (nodeId: string): Promise<boolean> => {
      return mergeNodeNeighborhood(nodeId);
    },
    [mergeNodeNeighborhood],
  );

  const expandMore = useCallback(async () => {
    if (!exploreRootId) {
      return;
    }

    const nextDepth = Math.min(exploreDepth + 1, 3);

    await replaceExploredGraph({ nodeId: exploreRootId, depth: nextDepth });
  }, [exploreDepth, exploreRootId, replaceExploredGraph]);

  useEffect(() => {
    if (
      hasAutoRunPresetRef.current ||
      isStatsLoading ||
      totalEntityCount === 0 ||
      !networkId
    ) {
      if (!isStatsLoading) {
        setIsInitialLoading(false);
      }

      return;
    }

    hasAutoRunPresetRef.current = true;

    const preset = resolveFirstAvailableExplorePreset({
      crownJewelCount,
      ownedPrincipalCount,
      skipCrownJewels: crownJewelCount === 0,
    });

    void (async () => {
      setIsInitialLoading(true);
      setIsError(false);

      try {
        await runPresetQuery(preset);
      } finally {
        setIsInitialLoading(false);
      }
    })();
  }, [
    crownJewelCount,
    ownedPrincipalCount,
    isStatsLoading,
    runPresetQuery,
    totalEntityCount,
    networkId,
  ]);

  const graph = exploredGraph;

  const isExploreEmpty =
    !isInitialLoading &&
    !isExpanding &&
    totalEntityCount > 0 &&
    (graph?.nodes.length ?? 0) === 0;

  const refetchInitial = useCallback(async () => {
    const { data: refreshedStats } = await refetchStats();

    hasAutoRunPresetRef.current = false;
    setExploredGraph(null);
    setActivePreset(null);
    setIsInitialLoading(true);

    const refreshedCrownJewelCount = refreshedStats?.crownJewelCount ?? 0;
    const refreshedOwnedPrincipalCount =
      refreshedStats?.ownedPrincipalCount ?? 0;

    const preset = resolveFirstAvailableExplorePreset({
      crownJewelCount: refreshedCrownJewelCount,
      ownedPrincipalCount: refreshedOwnedPrincipalCount,
      skipCrownJewels: refreshedCrownJewelCount === 0,
    });

    try {
      await runPresetQuery(preset);
    } finally {
      setIsInitialLoading(false);
      hasAutoRunPresetRef.current = true;
    }
  }, [refetchStats, runPresetQuery]);

  return {
    graph,
    isLoading: isInitialLoading && !graph,
    isExpanding,
    isError,
    exploreRootId,
    exploreDepth,
    activePreset,
    totalEntityCount,
    isExploreEmpty,
    crownJewelCount,
    ownedPrincipalCount,
    expandSelectedNode,
    exploreFromSearch,
    expandMore,
    runPresetQuery,
    resetExplore,
    refetchInitial,
  };
}
